import { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback } from 'react';
import { isEqual } from 'lodash-es';
import { toast } from 'react-toastify';
import { useDisclosure } from '@ds';
import { useSendSharedMessage } from '@features/components/Chat/components/hooks/useSendSharedMessage';
import { AppType, useAppContext } from '@features/contexts/AppProvider';
import { useStreamChatContext } from '@features/contexts/ChatProvider';
import { ShareToChatModal } from '@features/components/Chat/components/ShareToChatModal';
import { useChatChannelsState } from '@features/components/Chat/components/hooks/useChatChannelsState';
import { Channel } from 'stream-chat';
import { ShareToChatChatValues, PendingShare } from '../types';

type StreamChatShareContextValue = {
  channelsExist: boolean;
  pendingShare: PendingShare | null;
  chatValues: ShareToChatChatValues | null;
  setPendingShare: (share: PendingShare | null) => void;
  setInitData: (chatValues: ShareToChatChatValues) => void;
};

const StreamChatShareContext = createContext<StreamChatShareContextValue | null>(null);

export const StreamChatShareProvider = ({ children }: { children: ReactNode }) => {
  const { type: appType, chatConfig } = useAppContext();
  const { chatGrants, isConnected } = useStreamChatContext();
  const channels: Channel[] = useChatChannelsState(chatConfig.excludedChannelTypesToShare, chatGrants);
  const [pendingShareState, setPendingShareState] = useState<PendingShare | null>(null);
  const [chatValues, setChatValues] = useState<ShareToChatChatValues | null>(null);
  const {
    isOpen: isShareToChatModalOpened,
    onOpen: openShareToChatModal,
    onClose: closeShareToChatModal,
  } = useDisclosure();
  const { sendSharedDataToSpecificChat, sendMessage } = useSendSharedMessage({
    channels,
    pendingShare: pendingShareState,
    setPendingShare: setPendingShareState,
  });

  useEffect(() => {
    if (!pendingShareState) return;

    if (appType === AppType.NopAccessPlatform) {
      sendSharedDataToSpecificChat();
    } else {
      openShareToChatModal();
    }
  }, [appType, pendingShareState, sendSharedDataToSpecificChat, openShareToChatModal]);

  const handleCloseShareModal = () => {
    closeShareToChatModal();
    setPendingShareState(null);
  };

  const setInitData = useCallback(
    (chatValuesArg: ShareToChatChatValues) => {
      if (!isEqual(chatValues, chatValuesArg)) {
        setChatValues(chatValuesArg);
      }
    },
    [chatValues],
  );

  const setPendingShare = useCallback(
    (data: PendingShare | null) => {
      if (isConnected) {
        setPendingShareState(data);
      } else {
        toast.error('Failed to connect to chats. Please try reconnecting.');
      }
    },
    [isConnected],
  );

  const contextValue = useMemo(
    () => ({
      channelsExist: channels.length > 0,
      pendingShare: pendingShareState,
      chatValues,
      setPendingShare,
      setInitData,
    }),
    [channels, pendingShareState, chatValues, setInitData, setPendingShare],
  );

  return (
    <StreamChatShareContext.Provider value={contextValue}>
      {children}
      {chatValues && (
        <ShareToChatModal
          open={isShareToChatModalOpened}
          channels={channels}
          sharedContent={pendingShareState}
          onClose={handleCloseShareModal}
          modalProps={chatValues}
          sendMessage={sendMessage}
        />
      )}
    </StreamChatShareContext.Provider>
  );
};

export function useChatShare() {
  const context = useContext(StreamChatShareContext);
  if (!context) {
    throw new Error('useChatShare must be used inside <StreamChatShareProvider>');
  }
  return context;
}
